import React, { type ReactNode, useLayoutEffect } from 'react';

import { type MessageDescriptor, useIntl } from 'react-intl-next';

import EmptyState from '@atlaskit/empty-state';

import { useAnalyticsEvents } from '../../common/analytics/generated/use-analytics-events';

import { GenericErrorIcon } from './icon';
import messages from './messages';

export function ErrorBoundaryUI({
	errorFallbackUI,
	errorActionUI,
	location,
}: {
	errorActionUI?: ReactNode;
	errorFallbackUI?: ReactNode;
	location?: string;
}) {
	const intl = useIntl();
	const { fireEvent } = useAnalyticsEvents();

	useLayoutEffect(() => {
		fireEvent('ui.errorBoundary.viewed.errorBoundaryUI', {
			isFallbackUI: !!errorFallbackUI,
		});
	}, [errorFallbackUI, fireEvent]);

	// If a custom fallback UI is provided, render it directly at risk of consumer
	if (errorFallbackUI) {
		return errorFallbackUI;
	}

	return (
		<EmptyState
			testId="navigation-error-boundary-ui"
			header={intl.formatMessage(getErrorHeader(location, 'header'))}
			description={intl.formatMessage(getErrorHeader(location, 'description'))}
			headingLevel={4}
			headingSize="medium"
			primaryAction={errorActionUI}
			renderImage={() => <GenericErrorIcon />}
		/>
	);
}

function getErrorHeader(location?: string, type?: string): MessageDescriptor {
	switch (location) {
		case 'customization-modal':
			return type === 'header' ? messages.customizationHeader : messages.customizationDescription;
		default:
			return type === 'header' ? messages.header : messages.description;
	}
}
